import React, { useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { UploadCloud, X, Star } from "lucide-react";
import { toast } from "sonner";

export default function PhotoUpload({ paths = [], onChange, cta = "Drop or click to upload", testid = "photo-upload", cover = false }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const handle = async (files) => {
    if (!files.length) return;
    setBusy(true);
    const next = [...paths];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(data.path);
      } catch {
        toast.error("Upload failed for " + file.name);
      }
    }
    onChange(next);
    setBusy(false);
  };

  const makeCover = (i) => {
    if (i === 0) return;
    const next = [...paths];
    const [pick] = next.splice(i, 1);
    next.unshift(pick);
    onChange(next);
  };

  return (
    <div>
      <label
        data-testid={`${testid}-dropzone`}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
          handle(files);
        }}
        className={`flex flex-col items-center justify-center hairline py-6 cursor-pointer transition-colors ${drag ? "bg-black/[0.06] ring-1 ring-black/20" : "hover:bg-black/[0.02]"}`}>
        <UploadCloud size={20} strokeWidth={1.5} />
        <span className="mt-2 text-xs uppercase tracking-[0.2em]">{busy ? "Uploading…" : (drag ? "Drop to upload" : cta)}</span>
        <input type="file" multiple accept="image/*" className="hidden" data-testid={`${testid}-input`}
          onChange={(e) => handle(Array.from(e.target.files || []))} />
      </label>
      {cover && paths.length > 1 && (
        <p className="mt-2 text-xs text-neutral-500">Tap the star to pick your main photo — it shows first in search and on your seller page.</p>
      )}
      {paths.length > 0 && (
        <div className="mt-3 grid grid-cols-6 gap-2" data-testid={`${testid}-list`}>
          {paths.map((p, i) => (
            <div key={`${p}-${i}`} className="relative aspect-square hairline group">
              <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
              <button type="button" data-testid={`${testid}-remove-${i}`}
                onClick={() => onChange(paths.filter((_, idx) => idx !== i))}
                className="absolute top-1 right-1 bg-white hairline p-1">
                <X size={11} />
              </button>
              {cover && (
                i === 0 ? (
                  <span data-testid={`${testid}-cover-badge`}
                    className="absolute bottom-0 inset-x-0 bg-black/75 text-white text-[0.55rem] uppercase tracking-[0.15em] text-center py-0.5">
                    Main
                  </span>
                ) : (
                  <button type="button" title="Set as main photo" data-testid={`${testid}-cover-${i}`}
                    onClick={() => makeCover(i)}
                    className="absolute bottom-1 left-1 bg-white hairline p-1 opacity-90 hover:opacity-100">
                    <Star size={11} />
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
